<template>
  <div class="content-page">
    <MainHeader/>

    <main class="content-page-main">
      <section class="content-shell">
        <h1 class="content-title">{{ pageTitle }}</h1>

        <div v-if="isLoadingPages" class="content-state">
          Загружаем список страниц...
        </div>
        <div v-else-if="pagesError" class="content-state content-state--error">
          {{ pagesError }}
        </div>
        <div v-else-if="!pages.length" class="content-state">
          Нет доступных страниц.
        </div>

        <article v-else class="content-article">
          <div v-if="isLoadingPage" class="content-state">
            Загружаем страницу...
          </div>
          <div v-else-if="pageError" class="content-state content-state--error">
            {{ pageError }}
          </div>
          <div
              v-else-if="page?.content_html"
              class="wordpress-content"
              @click="handleWordPressContentClick"
              v-html="page.content_html"
          />
          <div v-else class="content-state">
            На этой странице пока нет контента.
          </div>
        </article>
      </section>
    </main>

    <div
        v-if="isImageModalOpen"
        class="image-modal"
        @click="closeImageModal"
    >
      <div class="image-modal__content" @click.stop>
        <button
            type="button"
            class="image-modal__close"
            aria-label="Закрыть просмотр изображения"
            @click="closeImageModal"
        >
          ×
        </button>
        <img
            class="image-modal__image"
            :src="activeImageSrc"
            alt=""
        >
      </div>
    </div>

    <Footer/>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import MainHeader from '@/components/layouts/MainHeader.vue'
import Footer from '@/components/layouts/Footer.vue'
import { fetchWordPressPage, fetchWordPressPages } from '@/utils/requests'

const route = useRoute()
const router = useRouter()

const pages = ref([])
const page = ref(null)
const isLoadingPages = ref(false)
const isLoadingPage = ref(false)
const pagesError = ref('')
const pageError = ref('')
const activeImageSrc = ref('')
const previousBodyOverflow = ref('')

const selectedSlug = computed(() => String(route.params.slug || '').trim().toLowerCase())
const pageTitle = computed(() => page.value?.title || 'Контент')
const isImageModalOpen = computed(() => Boolean(activeImageSrc.value))

const getErrorMessage = (payload, fallback) => {
  return payload?.data?.message || payload?.message || fallback
}

const parseResponse = async (response, fallback) => {
  const payload = await response.json().catch(() => ({}))

  if (!response.ok || payload?.success === false) {
    throw new Error(getErrorMessage(payload, fallback))
  }

  return payload?.data || {}
}

const getImageSource = (imgElement) => {
  if (!(imgElement instanceof HTMLImageElement)) {
    return ''
  }

  return imgElement.dataset.gallerySrc
      || imgElement.dataset.origFile
      || imgElement.currentSrc
      || imgElement.src
      || ''
}

const closeImageModal = () => {
  activeImageSrc.value = ''
}

const openImageModal = (source) => {
  if (!source) {
    return
  }

  activeImageSrc.value = source
}

const handleWordPressContentClick = (event) => {
  if (!(event.target instanceof Element)) {
    return
  }

  const trigger = event.target.closest('img[data-amportal-image], a[data-amportal-image-trigger]')
  if (!trigger) {
    return
  }

  const imgElement = trigger.matches('img[data-amportal-image]')
      ? trigger
      : trigger.querySelector('img[data-amportal-image]')
  const source = getImageSource(imgElement)

  if (!source) {
    return
  }

  event.preventDefault()
  openImageModal(source)
}

const handleKeyDown = (event) => {
  if (event.key === 'Escape' && isImageModalOpen.value) {
    closeImageModal()
  }
}

const openDefaultPageIfNeeded = async () => {
  if (selectedSlug.value || !pages.value.length) {
    return false
  }

  await router.replace({ name: 'Content', params: { slug: pages.value[0].slug } })
  return true
}

const loadPages = async () => {
  isLoadingPages.value = true
  pagesError.value = ''

  try {
    const response = await fetchWordPressPages()
    const data = await parseResponse(response, 'Не удалось загрузить список страниц.')
    pages.value = Array.isArray(data.pages) ? data.pages : []
  } catch (error) {
    pages.value = []
    pagesError.value = error.message || 'Не удалось загрузить список страниц.'
  } finally {
    isLoadingPages.value = false
  }
}

const loadPage = async (slug) => {
  if (!slug) {
    page.value = null
    pageError.value = ''
    return
  }

  isLoadingPage.value = true
  pageError.value = ''

  try {
    const response = await fetchWordPressPage(slug)
    const data = await parseResponse(response, 'Не удалось загрузить страницу.')
    page.value = data.page || null
  } catch (error) {
    page.value = null
    pageError.value = error.message || 'Не удалось загрузить страницу.'
  } finally {
    isLoadingPage.value = false
  }
}

const syncPageWithRoute = async () => {
  if (await openDefaultPageIfNeeded()) {
    return
  }

  if (!selectedSlug.value) {
    page.value = null
    pageError.value = ''
    return
  }

  const hasPageInList = pages.value.some(item => item.slug === selectedSlug.value)
  if (!hasPageInList && pages.value.length) {
    await router.replace({ name: 'Content', params: { slug: pages.value[0].slug } })
    return
  }

  await loadPage(selectedSlug.value)
}

onMounted(async () => {
  window.addEventListener('keydown', handleKeyDown)
  await loadPages()
  await syncPageWithRoute()
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeyDown)
  if (isImageModalOpen.value) {
    document.body.style.overflow = previousBodyOverflow.value
  }
})

watch(() => route.params.slug, async () => {
  await syncPageWithRoute()
})

watch(isImageModalOpen, (isOpen) => {
  if (isOpen) {
    previousBodyOverflow.value = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return
  }

  document.body.style.overflow = previousBodyOverflow.value
})
</script>

<style scoped>
.content-page-main {
  min-height: calc(100vh - 440px);
  background-color: #f8f8f8;
  padding: 24px 108px 40px;
}

.content-shell {
  max-width: 1080px;
  background-color: #ffffff;
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  padding: 24px;
}

.content-title {
  margin: 0 0 20px;
  font-size: 32px;
  line-height: 36px;
  color: #23282c;
}

.content-state {
  font-size: 16px;
  line-height: 22px;
  color: #23282c;
}

.content-state--error {
  color: #ba2a2a;
}

.content-article {
  min-height: 280px;
}

.wordpress-content :deep(*) {
  max-width: 100%;
}

.wordpress-content :deep(img) {
  height: auto;
}

.wordpress-content :deep(img[data-amportal-image]),
.wordpress-content :deep(a[data-amportal-image-trigger]) {
  cursor: zoom-in;
}

.image-modal {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.82);
}

.image-modal__content {
  position: relative;
  max-width: 100%;
  max-height: 100%;
}

.image-modal__close {
  position: fixed;
  top: 20px;
  right: 20px;
  border: 0;
  background: transparent;
  color: #ffffff;
  font-size: 36px;
  line-height: 1;
  cursor: pointer;
  z-index: 2001;
}

.image-modal__image {
  display: block;
  max-width: min(1280px, calc(100vw - 48px));
  max-height: calc(100vh - 64px);
  width: auto;
  height: auto;
  object-fit: contain;
}

@media screen and (max-width: 1200px) {
  .content-page-main {
    padding: 20px 24px 36px;
  }
}
</style>
